import { Component } from '@theme/component';

/**
 * Announcement bar.
 *
 * A crossfade rotator for short promotional messages, sitting above the header.
 * Horizon's native `<slideshow-component>` is a scroll-snap carousel and cannot
 * crossfade, so this mirrors the same public API instead - `select(index, event,
 * options)`, `next`, `previous`, `play`, `pause`, `resume`, `suspend` and the
 * `paused` attribute - so it answers the contract `assets/theme-editor.js` uses
 * (HORIZON-SPEC 10.2).
 *
 * Autoplay is suspended while the bar is off-screen, while the tab is hidden,
 * while the pointer is over the bar, while a pointer drag is in flight, and
 * whenever the visitor asks for reduced motion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The crossfade viewport.
 * @property {HTMLElement[]} [slides] - The message slides.
 * @property {HTMLButtonElement} [pauseButton] - The visually hidden play/pause toggle.
 * @property {HTMLElement} [pauseLabel] - The label inside the play/pause toggle.
 * @property {HTMLElement} [live] - The aria-live status region.
 *
 * @extends Component<Refs>
 */
export class OcAnnouncementBarComponent extends Component {
  requiredRefs = ['viewport'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {AbortController | null} */
  #dragAbort = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {MediaQueryList | null} */
  #reducedMotion = null;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  #index = 0;
  #visible = true;
  #hovered = false;
  #dragging = false;
  #startX = 0;
  #deltaX = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange, { signal });

    document.addEventListener('visibilitychange', this.#handleVisibility, { signal });

    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    this.addEventListener('focusin', this.#handlePointerEnter, { signal });
    this.addEventListener('focusout', this.#handlePointerLeave, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, { signal });
    }

    this.refs.viewport?.classList.toggle('is-draggable', this.slides.length > 1);

    this.#observeViewport();
    this.#paint();
    this.#sync();
  }

  disconnectedCallback() {
    this.suspend();
    this.#abort?.abort();
    this.#abort = null;
    this.#dragAbort?.abort();
    this.#dragAbort = null;
    this.#observer?.disconnect();
    this.#observer = null;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback?.();

    if (this.#index > this.slides.length - 1) this.#index = 0;
    this.refs.viewport?.classList.toggle('is-draggable', this.slides.length > 1);
    this.#paint(false);
    this.#sync();
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return this.refs.slides ?? [];
  }

  /** The current message index. */
  get current() {
    return this.#index;
  }

  /** Autoplay interval in milliseconds, or `undefined` when autoplay is off. */
  get autoplayInterval() {
    const value = parseFloat(`${this.getAttribute('autoplay')}`);

    if (Number.isNaN(value) || value <= 0) return undefined;

    return value * 1000;
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /* ------------------------------------------------------------------ *
   * Playback
   * ------------------------------------------------------------------ */

  /**
   * Start automatic playback.
   * @param {number} [interval] - Interval in milliseconds.
   */
  play(interval = this.autoplayInterval) {
    this.suspend();

    if (!interval) return;
    if (this.slides.length < 2) return;
    if (this.paused) return;
    if (this.#reducedMotion?.matches) return;
    if (this.#hovered || this.#dragging) return;
    if (!this.#visible || document.hidden) return;

    this.#timer = setInterval(() => this.next(), interval);
  }

  /** Stop the timer without changing the paused state. */
  suspend() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  /** Pause automatic playback. */
  pause() {
    this.paused = true;
    this.suspend();
  }

  /** Resume automatic playback if autoplay is enabled. */
  resume() {
    this.paused = false;
    this.play();
  }

  /** Re-evaluate whether the timer should be running. */
  #sync() {
    if (this.paused) {
      this.suspend();
      return;
    }

    this.play();
  }

  /** Toggle playback from the visually hidden pause button. */
  togglePause() {
    if (this.paused) {
      this.resume();
    } else {
      this.pause();
    }

    const { pauseButton, pauseLabel } = /** @type {any} */ (this.refs);

    if (pauseButton) {
      pauseButton.setAttribute('aria-pressed', this.paused ? 'true' : 'false');

      const label = pauseLabel ?? pauseButton.querySelector('span');
      if (label) {
        label.textContent = this.paused
          ? pauseButton.dataset.playLabel || 'Play slideshow'
          : pauseButton.dataset.pauseLabel || 'Pause slideshow';
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * Selection
   * ------------------------------------------------------------------ */

  /**
   * Select a message.
   * @param {number} index
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  select(index, event, { animate = true } = {}) {
    event?.preventDefault?.();

    const total = this.slides.length;
    if (!total) return;

    const target = ((Number(index ?? 0) % total) + total) % total;

    this.#index = target;
    this.#paint(animate);

    if (event) this.#sync();
  }

  /**
   * Go to the next message.
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  next(event, options) {
    this.select(this.#index + 1, event, options);
  }

  /**
   * Go to the previous message.
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  previous(event, options) {
    this.select(this.#index - 1, event, options);
  }

  /**
   * @param {boolean} [animate]
   */
  #paint(animate = true) {
    const slides = this.slides;
    const { live, viewport } = this.refs;

    if (!animate && viewport) {
      viewport.classList.add('is-instant');
      requestAnimationFrame(() => requestAnimationFrame(() => viewport.classList.remove('is-instant')));
    }

    slides.forEach((slide, i) => {
      const on = i === this.#index;
      slide.classList.toggle('is-selected', on);
      slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      slide.style.opacity = '';
      slide.style.zIndex = '';

      // Keep hidden messages out of the tab order.
      for (const link of slide.querySelectorAll('a')) {
        if (on) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      }
    });

    const template = this.dataset.statusTemplate;
    if (live && template) {
      live.textContent = template.replace('__index__', String(this.#index + 1));
    }
  }

  /* ------------------------------------------------------------------ *
   * Keyboard
   * ------------------------------------------------------------------ */

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next(event);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous(event);
    }
  }

  /* ------------------------------------------------------------------ *
   * Drag / swipe
   * ------------------------------------------------------------------ */

  /**
   * @param {PointerEvent} event
   */
  handlePointerDown(event) {
    if (this.slides.length < 2) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#dragging = true;
    this.#startX = event.clientX;
    this.#deltaX = 0;
    this.suspend();
    this.refs.viewport?.classList.add('is-dragging');

    this.#dragAbort?.abort();
    this.#dragAbort = new AbortController();
    const { signal } = this.#dragAbort;

    window.addEventListener('pointermove', this.#handlePointerMove, { signal });
    window.addEventListener('pointerup', this.#handlePointerUp, { signal });
    window.addEventListener('pointercancel', this.#handlePointerUp, { signal });
  }

  /**
   * @param {number} direction
   * @returns {HTMLElement | undefined}
   */
  #neighbour(direction) {
    const total = this.slides.length;
    if (!total) return undefined;
    return this.slides[(((this.#index + direction) % total) + total) % total];
  }

  #handlePointerMove = (/** @type {PointerEvent} */ event) => {
    if (!this.#dragging) return;

    this.#deltaX = event.clientX - this.#startX;

    if (this.#reducedMotion?.matches) return;

    const width = this.refs.viewport?.offsetWidth || 1;
    const progress = Math.max(-1, Math.min(1, this.#deltaX / width));
    const amount = Math.abs(progress);
    const current = this.slides[this.#index];
    const other = this.#neighbour(progress < 0 ? 1 : -1);

    if (current) current.style.opacity = String(1 - amount);

    if (other && other !== current) {
      other.style.opacity = String(amount);
      other.style.zIndex = '1';
    }
  };

  #handlePointerUp = () => {
    if (!this.#dragging) return;
    this.#dragging = false;

    this.#dragAbort?.abort();
    this.#dragAbort = null;

    this.refs.viewport?.classList.remove('is-dragging');

    for (const slide of this.slides) {
      slide.style.opacity = '';
      slide.style.zIndex = '';
    }

    const threshold = Math.min(80, (this.refs.viewport?.offsetWidth ?? 0) * 0.08);

    if (this.#deltaX < -threshold) {
      this.next();
    } else if (this.#deltaX > threshold) {
      this.previous();
    } else {
      this.#paint();
    }

    this.#sync();
  };

  /* ------------------------------------------------------------------ *
   * Visibility / hover
   * ------------------------------------------------------------------ */

  #observeViewport() {
    if (!('IntersectionObserver' in window)) {
      this.#visible = true;
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#visible = entry.isIntersecting;
        }
        this.#sync();
      },
      { threshold: 0.1 }
    );

    this.#observer.observe(this);
  }

  #handleVisibility = () => {
    this.#sync();
  };

  #handleMotionChange = () => {
    this.#sync();
  };

  #handlePointerEnter = () => {
    this.#hovered = true;
    this.suspend();
  };

  #handlePointerLeave = () => {
    this.#hovered = false;
    this.#sync();
  };

  /* ------------------------------------------------------------------ *
   * Theme editor (HORIZON-SPEC 10.2)
   * ------------------------------------------------------------------ */

  #handleBlockSelect = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const slide = event.target.closest('[data-oc-announcement]');
    if (!slide || slide.closest('oc-announcement-bar-component') !== this) return;

    const index = Number(slide.getAttribute('data-index') ?? 0);
    const isAlreadyActive = index === this.#index;

    this.pause();
    this.select(index, undefined, { animate: !isAlreadyActive });
  };

  #handleBlockDeselect = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('oc-announcement-bar-component') !== this) return;

    this.resume();
  };
}

if (!customElements.get('oc-announcement-bar-component')) {
  customElements.define('oc-announcement-bar-component', OcAnnouncementBarComponent);
}